import { ShotMetadata } from '@nrwl/rx-models';

import { Widget } from './form-widget.model';
import { parseLocalDateString } from '../helpers/date-utils';

type ShotFieldSpec = {
  transform: (value: unknown) => string | number | null;
};

export class FbEmrShotsWidget extends Widget {
  private readonly SHOT_SCHEMA: Record<keyof ShotMetadata, ShotFieldSpec> = {
    id: { transform: (value) => value as string },
    date: { transform: (value) => this.parseDateOrEmpty(value) },
    expire: { transform: (value) => this.parseDateOrEmpty(value) },
    compound: { transform: (value) => this.asTrimmedString(value) },
    amount: { transform: (value) => value as number },
    uom: { transform: (value) => this.asTrimmedString(value) },
    site: { transform: (value) => this.asTrimmedString(value) },
    employee_id: { transform: (value) => this.asTrimmedString(value) },
    lot_number: { transform: (value) => this.asTrimmedString(value) },
    notes: { transform: (value) => this.asTrimmedString(value) },
  };

  constructor(data?: unknown) {
    super(data);

    this.name = 'fb-emr-shots';
    this.title = 'Shots';

    this.properties.text ??= { title: 'Header', value: 'Shots' };
  }

  public getMetadata(): Record<string, unknown> {
    if (this.properties?.items?.hidden) {
      return {};
    }

    const value = this.properties?.items?.value;

    if (!Array.isArray(value)) {
      return {};
    }

    const shots = value
      .map((item: ShotMetadata) => this.buildShot(item))
      .filter((shot) => this.isValidShot(shot));

    return shots.length ? { shots: shots as ShotMetadata[] } : { shots: [] };
  }

  public isValid(): boolean {
    if (this.properties?.items?.hidden) {
      return true;
    }

    return !this.properties.items?.formInvalid;
  }

  private buildShot(data: ShotMetadata): ShotMetadata {
    const shot: ShotMetadata = {};

    Object.keys(this.SHOT_SCHEMA).forEach((field) => {
      const schema = this.SHOT_SCHEMA[field];
      const raw = data[field];
      const value = schema.transform(raw);

      if (this.hasValue(value)) {
        shot[field] = value;
      }
    });

    return shot;
  }

  private isValidShot(shot: ShotMetadata): boolean {
    return Object.values(shot).some((value) => this.hasValue(value));
  }

  private hasValue(value: unknown): boolean {
    return !!value && (typeof value !== 'string' || value.trim() !== '');
  }

  private asTrimmedString(v: unknown): string {
    if (v === null || v === undefined) {
      return '';
    }

    return String(v).trim();
  }

  private parseDateOrEmpty(dateValue: unknown): string {
    return dateValue instanceof Date || typeof dateValue === 'string'
      ? parseLocalDateString(dateValue)
      : '';
  }
}
